// ============================================================
// Field Display Names (ชื่อที่แสดงใน SharePoint column)
// graphService จะ auto-map ชื่อเหล่านี้ → internal hex name
// ============================================================

import { setServerDirectory } from "./peopleDb.js";
export const DISPLAY_NAMES = {
  customerName:   "ชื่อลูกค้า",
  workDetail:     "รายละเอียดงาน",
  ownerName:      "เจ้าของงาน",             // Person field
  supervisor:     "ผู้ดูแลหลัก",             // Person field
  actionDetail:   "รายละเอียดการดำเนินการ",
  status:         "สถานะ",
  taskType:       "ประเภทงาน",
  managementType: "การจัดการ",
  requestDate:    "วันที่แจ้ง",
  actionDate:     "วันที่เข้าดำเนินการ",
  reporter:       "ผู้แจ้งงาน",              // Person field
  related:        "ผู้เกี่ยวข้อง",           // Multi-Person field
  estimatedValue: "มูลค่างานที่คาดหวังโดยประมาณ",
  winChance:      "โอกาศได้งาน (0-5)",
  location:       "สถานที่",                 // Location field
  processType:    "ประเภทกระบวนการ",
  projectName:    "ชื่อโครงการ/งาน",
};

// ─── Column Map (displayName → internalHexName) ─────────────
let _columnMap = {};
export function setColumnMap(map) {
  _columnMap = map;
  console.log("[ColumnMap] total:", Object.keys(map).length);
}
export function getColumnMap() { return _columnMap; }

// ─── User Map (SharePoint LookupId → { name, dept, email }) ─
// หมายเหตุ: ให้ผ่าน applyCustomToUserMap() จาก peopleDb ก่อนเสมอ
let _userMap = {};
export function setUserMap(map)         { _userMap = map; }
export function resolveUserId(id)       { return _userMap[String(id)] ?? null; }
export function getUserMap()            { return _userMap; }

let _peopleDirectoryIndexes = { byEmail: new Map(), byName: new Map(), byDeptDivision: new Map() };

function norm(value) {
  return String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

export function setPeopleDirectory(people) {
  setServerDirectory(people);
  const byEmail = new Map();
  const byName = new Map();
  const deptDivisionCandidates = new Map();
  (people || []).forEach(person => {
    const email = norm(person?.email);
    if (email) byEmail.set(email, person);
    [
      person?.name,
      person?.nameApi,
      person?.sheetName,
      person?.nickname,
      person?.nicknames,
    ].forEach(name => {
      const key = norm(name);
      if (key) byName.set(key, person);
    });

    const division = String(person?.division || "").trim();
    [person?.dept, person?.deptSheet, person?.deptApi, person?.deptCustom].forEach(dept => {
      const key = norm(dept);
      if (!key || !division) return;
      if (!deptDivisionCandidates.has(key)) deptDivisionCandidates.set(key, new Set());
      deptDivisionCandidates.get(key).add(division);
    });
  });

  const byDeptDivision = new Map();
  deptDivisionCandidates.forEach((divisions, dept) => {
    if (divisions.size === 1) byDeptDivision.set(dept, [...divisions][0]);
  });
  _peopleDirectoryIndexes = { byEmail, byName, byDeptDivision };
}

function rawPersonFieldValue(item, key) {
  const displayName = DISPLAY_NAMES[key];
  if (!displayName) return null;
  return item?.[resolve(displayName)] ?? null;
}

function resolvePersonDirectoryPerson(item, personKey, user) {
  const raw = rawPersonFieldValue(item, personKey);
  const emailCandidates = [user?.email, raw?.Email, raw?.email].map(norm).filter(Boolean);
  for (const email of emailCandidates) {
    const person = _peopleDirectoryIndexes.byEmail.get(email);
    if (person) return person;
  }

  const nameCandidates = [
    user?.name,
    user?.nameApi,
    raw?.LookupValue,
    raw?.displayName,
    raw?.title,
    raw?.Title,
    typeof raw === "string" ? raw : "",
  ].map(norm).filter(Boolean);
  for (const name of nameCandidates) {
    const person = _peopleDirectoryIndexes.byName.get(name);
    if (person) return person;
  }
  return null;
}

// ─── Photo Map (SharePoint LookupId → blob URL) ─────────────
let _photoMap = {};
export function setPhotoMap(map)        { _photoMap = map; }
export function getPhoto(userId)        { return _photoMap[String(userId)] ?? null; }
export function getPhotoMap()           { return _photoMap; }

// ─── resolve displayName → internalName ─────────────────────
function resolve(displayName) {
  return _columnMap[displayName] ?? displayName;
}

// ─── gf: ดึงค่าจาก item ด้วย key ───────────────────────────
export function gf(item, key) {
  const displayName = DISPLAY_NAMES[key];
  if (!displayName) return "";
  const internalName = resolve(displayName);
  let val = item?.[internalName];

  // Person field → ลอง LookupId → resolve ชื่อจาก userMap
  if (val === null || val === undefined) {
    const lookupId = item?.[internalName + "LookupId"];
    if (lookupId != null) {
      const user = resolveUserId(lookupId);
      if (user?.name) return user.name;
      return `User #${lookupId}`;
    }
  }

  if (val === null || val === undefined) return "";
  if (typeof val === "object") {
    return val?.displayName ?? val?.title ?? val?.Title ?? val?.LookupValue ?? JSON.stringify(val).slice(0, 80);
  }
  return String(val);
}

// ─── gfLookupId: ดึง SharePoint User ID จาก Person field ───
export function gfLookupId(item, key) {
  const displayName = DISPLAY_NAMES[key];
  if (!displayName) return null;
  const internalName = resolve(displayName);
  const id = item?.[internalName + "LookupId"];
  return id != null ? String(id) : null;
}

// ─── gfDept: ดึง department จาก Person field ผ่าน userMap ──
export function gfDept(item, personKey) {
  const id = gfLookupId(item, personKey);
  if (!id) return "";
  return resolveUserId(id)?.dept ?? "";
}

// ─── gfDivision: ดึงฝ่ายหลักจากฐานบุคคลเท่านั้น ไม่ fallback เป็นแผนก ──
export function gfDivision(item, personKey) {
  const id = gfLookupId(item, personKey);
  const user = id ? resolveUserId(id) : null;
  const person = resolvePersonDirectoryPerson(item, personKey, user);
  if (person?.division) return person.division;
  if (user?.division) return user.division;

  const deptDivision = _peopleDirectoryIndexes.byDeptDivision.get(norm(user?.dept));
  if (deptDivision) return deptDivision;
  return "";
}

// ─── gfMultiPerson: ดึง Multi-Person field → array of { name, userId } ──────
// SharePoint multi-person field เก็บเป็น Array of { LookupId, LookupValue, Email }
export function gfMultiPerson(item, key) {
  const displayName = DISPLAY_NAMES[key];
  if (!displayName) return [];
  const internalName = resolve(displayName);
  const val = item?.[internalName];

  // Array of { LookupId, LookupValue, Email }
  if (Array.isArray(val)) {
    return val.map(v => {
      const id   = v.LookupId != null ? String(v.LookupId) : null;
      const user = id ? resolveUserId(id) : null;
      return {
        name:   user?.name || v.LookupValue || (id ? `User #${id}` : "?"),
        userId: id,
      };
    }).filter(v => v.name);
  }

  // Single object { LookupId, LookupValue }
  if (val && typeof val === "object") {
    const id   = val.LookupId != null ? String(val.LookupId) : null;
    const user = id ? resolveUserId(id) : null;
    return [{ name: user?.name || val.LookupValue || val.displayName || "", userId: id }].filter(v => v.name);
  }

  // Fallback: ลอง single LookupId
  const lookupId = item?.[internalName + "LookupId"];
  if (lookupId != null) {
    const id   = String(lookupId);
    const user = resolveUserId(id);
    return [{ name: user?.name || `User #${id}`, userId: id }];
  }

  return [];
}

// ─── gfRaw: ดึงค่าแบบ raw ───────────────────────────────────
export function gfRaw(item, key) {
  const displayName = DISPLAY_NAMES[key];
  if (!displayName) return undefined;
  return item?.[resolve(displayName)];
}

// ─── Filter Constants ────────────────────────────────────────
export const MANAGEMENT_ON_TRACK  = "On Track";
export const MANAGEMENT_GROWTH    = "Growth project";
export const MANAGEMENT_STRATEGIC = "Strategic Project";
export const STATUS_COMPLETE_WIN  = "เสร็จสิ้น [ได้งาน]";
export const STATUS_COMPLETE_LOSE = "เสร็จสิ้น [ไม่ได้งาน]";

// ─── Status flow (เรียงตามลำดับงานจริง — ใช้เป็น choices ในฟอร์ม) ──
// flow รวมงานทั่วไป + งานโครงการ/ราชการ ไว้ใน field เดียว ไม่บังคับซ่อนตามประเภทกระบวนการ
export { LINE_OA_STATUSES as ALL_STATUSES } from "../api/_lib/lineOaFollowUpPolicy.js";

export const STATUS_COLORS = {
  "รับแจ้งใหม่": "#1565C0",
  "กำลังตรวจสอบ": "#6D4C41",
  "รอติดตาม": "#F9A825",
  "ส่งต่อเตรียมเปิดโครงการ": "#2E7D32",
  "ส่งต่อ Demo": "#7B1FA2",
  "ปิดงานรับแจ้ง": "#455A64",
  "เสร็จสิ้น":              "#2E7D32",
  [STATUS_COMPLETE_WIN]:  "#2E7D32",
  [STATUS_COMPLETE_LOSE]: "#C62828",
  "เลื่อนออกไป":           "#F9A825",
  "ยกเลิก":                "#757575",
};

// ─── ประเภทกระบวนการ ──────────────────────────────────────────
export const PROCESS_TYPE_PROJECT = "งานโครงการ-ราชการ";
export const PROCESS_TYPE_GENERAL = "งานขายทั่วไป";
export const PROCESS_TYPE_VISIT   = "นัดหมายทั่วไป-เข้าพบ";
export const ALL_PROCESS_TYPES = [PROCESS_TYPE_PROJECT, PROCESS_TYPE_GENERAL, PROCESS_TYPE_VISIT];

export const PROCESS_TYPE_COLORS = {
  [PROCESS_TYPE_PROJECT]: "#7B1FA2",
  [PROCESS_TYPE_GENERAL]: "#1565C0",
  [PROCESS_TYPE_VISIT]:   "#00897B",
};

// ─── Auto-color (สีอัตโนมัติสำหรับแผนกที่ไม่ได้กำหนด) ──────
const AUTO_PALETTE = [
  "#00ACC1","#00897B","#43A047","#7CB342","#C0CA33",
  "#F9A825","#FB8C00","#6D4C41","#546E7A","#D81B60",
  "#8E24AA","#3949AB","#039BE5","#00BFA5","#FF5722",
];

function hashStr(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return Math.abs(h);
}

// colorOf: ถ้าไม่มีใน map → generate สีจาก hash ชื่อแผนก (consistent)
export function colorOf(map, key) {
  if (!key || key === "ไม่ระบุ") return "#9E9E9E";
  if (map && map[key]) return map[key];
  return AUTO_PALETTE[hashStr(key) % AUTO_PALETTE.length];
}

// ─── Static Color Palettes (สำหรับแผนกที่รู้จัก) ─────────────
export const OWNER_DEPT_COLORS = {
  "พัฒนาธุรกิจ": "#3949AB",
  "สนับสนุนงานขาย": "#00ACC1",
  "บริการและปฏิบัติการองค์กร": "#7A4A3A",
  "เทคโนโลยี": "#00897B",
  "เทคโนโลยี กลุ่ม 1": "#1565C0",
  "เทคโนโลยี กลุ่ม 2": "#5E35B1",
  "ธุรกิจการค้า 1": "#E65100",
  "ธุรกิจการค้า 2": "#7B1FA2",
  "ธุรกิจการค้า 3": "#C2185B",
  "ทรัพยากรบุคคล": "#2E7D32",
  "บัญชีและการเงิน": "#F9A825",
  "จัดซื้อ": "#546E7A",
  "การขายและการตลาด": "#3949AB",
  "สนับสนุนงานขาย": "#00ACC1",
  "บริการงานไอที": "#43A047",
  "Business Development": "#00B0F0",
  "บริหารโครงการ": "#E91E8C",
  "PM": "#00897B",
  "Project": "#7CB342",
  "Sales":           "#FF8C00",
  "Sales 1":         "#FF1493",
  "Sales 3":         "#C2185B",
  "Sale":            "#1565C0",
  "เว้นว่าง":        "#00B0F0",
};

export const SUPERVISOR_DEPT_COLORS = {
  "พัฒนาธุรกิจ": "#3949AB",
  "สนับสนุนงานขาย": "#00ACC1",
  "บริการและปฏิบัติการองค์กร": "#7A4A3A",
  "เทคโนโลยี": "#00897B",
  "เทคโนโลยี กลุ่ม 1": "#1565C0",
  "เทคโนโลยี กลุ่ม 2": "#5E35B1",
  "ธุรกิจการค้า 1": "#E65100",
  "ธุรกิจการค้า 2": "#7B1FA2",
  "ธุรกิจการค้า 3": "#C2185B",
  "ทรัพยากรบุคคล": "#2E7D32",
  "บัญชีและการเงิน": "#F9A825",
  "จัดซื้อ": "#546E7A",
  "การขายและการตลาด": "#3949AB",
  "สนับสนุนงานขาย": "#00ACC1",
  "บริการงานไอที": "#43A047",
  "บริหารโครงการ": "#E91E8C",
  "PM": "#00897B",
  "Project": "#7CB342",
  "Business Development":              "#00B0F0",
  "IT Service":                        "#1565C0",
  "Sale Support":                      "#FF8C00",
  "Sale Support & Project Management": "#7B1FA2",
  "บริหารโครงการ":                     "#E91E8C",
  "IT":                                "#0288D1",
};

export const TASK_TYPE_COLORS = {
  "GPS/MDVR":           "#00B0F0",
  "AIoT/Smart city/SI": "#1565C0",
  "ทั่วไป":             "#FF8C00",
  "Software":           "#7B1FA2",
  "Training":           "#E91E8C",
};

export const MANAGEMENT_TYPE_COLORS = {
  "On Track":         "#00B0F0",
  "Growth project":   "#1565C0",
  "Strategic Project":"#FF8C00",
  "Suspended":        "#7B1FA2",
  "Discontinued":     "#E91E8C",
};

export const STATUS_COLOR = "#2196F3";

// ─── Date Helpers ────────────────────────────────────────────
export const THAI_MONTHS = [
  "ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.",
  "ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค.",
];

export function monthLabel(ym) {
  if (!ym || ym === "all") return "ทุกเดือน";
  const [y, m] = ym.split("-");
  return `${THAI_MONTHS[parseInt(m, 10) - 1]} ${parseInt(y, 10) + 543}`;
}

export function thaiDate(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  if (isNaN(d)) return "—";
  return `${d.getDate()} ${THAI_MONTHS[d.getMonth()]} ${d.getFullYear() + 543}`;
}

// ─── googleMapsUrl: สร้างลิงก์ค้นหาสถานที่จากชื่อ/ที่อยู่ ──────
// ไม่ต้องมีพิกัด lat/lng — Google ค้นหาชื่อสถานที่ให้เองตอนเปิดลิงก์
export function googleMapsUrl(text) {
  if (!text) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;
}
